import React, { useMemo } from 'react';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { useSettings } from '../context/SettingsContext';
import { formatDate } from '../utils/dateFormatter';
import { formatCurrency } from '../utils/currencyUtils';
import { CrmButton, CrmTooltip } from './ui';
import { CrmEntityListPage } from './listing';

const getCustomerKey = (customer) => (
  customer.customer_email || customer.customer_phone || customer.customer_name
);

const exportCustomers = (rows, filename) => {
  const worksheet = XLSX.utils.json_to_sheet(rows.map((customer) => ({
    Name: customer.customer_name,
    Email: customer.customer_email,
    Phone: customer.customer_phone,
    'Total orders': customer.total_orders ?? customer.total_invoices ?? 0,
    'Website orders': customer.website_orders || 0,
    'Catering orders': customer.catering_orders || 0,
    'Total spent': customer.total_spent || 0,
    'Last transaction': formatDate(customer.last_transaction_date),
  })));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Customers');
  XLSX.writeFile(workbook, filename);
};

function CustomersTable({ customers = [], loading = false, error = '', onRetry, onRefresh }) {
  const navigate = useNavigate();
  const { settings } = useSettings();

  const openCustomer = (customer) => {
    const params = new URLSearchParams();
    if (customer.customer_email) params.set('email', customer.customer_email);
    else if (customer.customer_phone) params.set('phone', customer.customer_phone);
    else if (customer.customer_name) params.set('name', customer.customer_name);
    navigate(`/customers/detail?${params.toString()}`);
  };

  const columns = useMemo(() => [
    {
      id: 'customer_name', label: 'Name', width: 240, minWidth: 180, maxWidth: 420,
      hideable: false, defaultPinned: true, pinnable: true, stopRowClick: true,
      accessor: (customer) => customer.customer_name,
      renderCell: (customer) => (
        <button type="button" className="crm-table-primary-link" onClick={() => openCustomer(customer)}>
          {customer.customer_name || '—'}
        </button>
      ),
    },
    { id: 'customer_email', label: 'Email', width: 240, minWidth: 180, maxWidth: 420, accessor: (customer) => customer.customer_email },
    { id: 'customer_phone', label: 'Phone', width: 170, minWidth: 140, maxWidth: 260, accessor: (customer) => customer.customer_phone },
    {
      id: 'total_orders', label: 'Total orders', width: 140, minWidth: 120, maxWidth: 200, type: 'number',
      accessor: (customer) => Number(customer.total_orders ?? customer.total_invoices ?? 0),
    },
    {
      id: 'channels', label: 'Channels', width: 250, minWidth: 190, maxWidth: 360, sortable: false,
      renderCell: (customer) => (
        <span className="crm-channel-list">
          <span>Website {Number(customer.website_orders || 0)}</span>
          <span>Catering {Number(customer.catering_orders || 0)}</span>
          {Number(customer.other_orders || 0) > 0 ? <span>Other {Number(customer.other_orders)}</span> : null}
        </span>
      ),
    },
    {
      id: 'total_spent', label: 'Total spent', width: 170, minWidth: 140, maxWidth: 260, type: 'currency',
      accessor: (customer) => Number(customer.total_spent || 0),
      renderCell: (customer) => formatCurrency(customer.total_spent, settings?.currency_code),
    },
    {
      id: 'last_transaction_date', label: 'Last transaction', width: 180, minWidth: 150, maxWidth: 260,
      type: 'date', filterable: true, accessor: (customer) => customer.last_transaction_date,
      renderCell: (customer) => formatDate(customer.last_transaction_date) || '—',
    },
    {
      id: 'row_actions', label: 'Actions', width: 92, minWidth: 92, maxWidth: 120,
      hideable: false, sortable: false, searchable: false, className: 'crm-data-table__actions-cell',
      renderCell: (customer) => (
        <CrmTooltip content="View customer">
          <CrmButton iconOnly size="small" variant="tertiary" className="crm-row-action" onClick={() => openCustomer(customer)} aria-label="View customer">
            <VisibilityOutlinedIcon />
          </CrmButton>
        </CrmTooltip>
      ),
    },
  ], [settings?.currency_code]);

  const filters = useMemo(() => [
    { id: 'last_transaction', columnId: 'last_transaction_date', label: 'Last transaction', type: 'date-range', quick: true },
  ], []);

  return (
    <CrmEntityListPage
      moduleKey="customers"
      title="Customers"
      itemLabel="customer"
      items={customers}
      columns={columns}
      filterDefinitions={filters}
      getRowId={getCustomerKey}
      initialSort={{ columnId: 'last_transaction_date', direction: 'desc' }}
      loading={loading}
      error={error}
      onRetry={onRetry}
      onRefresh={onRefresh}
      onRowClick={openCustomer}
      onExport={(rows) => exportCustomers(rows, 'customers.xlsx')}
      onExportSelected={(rows) => exportCustomers(rows, 'selected_customers.xlsx')}
    />
  );
}

export default CustomersTable;
